import { useState, useEffect, useMemo } from "react";
import type { BaseEntity } from "../../UniversalCRMTable/types";

interface UseInitialLoadTrackingProps<T extends BaseEntity> {
  data: any;
  filterContext: any;
}

/**
 * Tracks initial load state and manages accumulated data for infinite scroll
 * Resets accumulated data when filters change
 */
export function useInitialLoadTracking<T extends BaseEntity>({
  data,
  filterContext,
}: UseInitialLoadTrackingProps<T>) {
  // Track whether data has loaded at least once to distinguish between
  // initial load (show full-page loading) and filter updates (show overlay)
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // Track accumulated data for infinite scroll
  const [accumulatedData, setAccumulatedData] = useState<any[]>([]);

  // Set hasLoadedOnce to true after first data arrives
  useEffect(() => {
    if (data?.data && data.data.length > 0 && !hasLoadedOnce) {
      setHasLoadedOnce(true);
      // Reset accumulated data on initial load
      setAccumulatedData(data.data);
    }
  }, [data?.data, hasLoadedOnce]);

  // Compared by VALUE, not identity. `filters.simple` and `filters.advanced` are
  // objects, so depending on them directly meant that any provider handing down
  // a freshly-built filter object — a non-memoized context value, a filter
  // context rebuilt inside a render — re-ran this effect and threw away every
  // infinite-scroll page the user had loaded. The list blanks and refetches from
  // page one, with nothing in the UI to explain it. Safe today only because
  // `FilterContext` happens to hold `filters` in `useState`; that is a property
  // of one provider, not an invariant of the hook.
  const filterKey = useMemo(
    () =>
      JSON.stringify({
        search: filterContext?.filters?.search ?? "",
        simple: filterContext?.filters?.simple ?? {},
        advanced: filterContext?.filters?.advanced ?? [],
      }),
    [
      filterContext?.filters?.search,
      filterContext?.filters?.simple,
      filterContext?.filters?.advanced,
    ]
  );

  // When filters or search actually change, reset accumulated data
  useEffect(() => {
    setAccumulatedData([]);
  }, [filterKey]);

  return {
    hasLoadedOnce,
    accumulatedData,
    setAccumulatedData,
  };
}
